import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mfa) {
        await verifyMfa(mfa.challenge_id, code);
        navigate(from, { replace: true });
        return;
      }
      const result = await login(email, password);
      if (result.mfa) {
        setMfa(result.mfa);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-white" data-testid="admin-login-page">
      <div className="lg:col-span-5 bg-obsidian text-white p-10 lg:p-16 flex flex-col justify-between min-h-[40vh]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 bg-execution" />
          <span className="font-heading text-xl font-bold tracking-tight">Nexra</span>
        </div>
        <div>
          <div className="overline mb-4">Admin Portal</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-bold tracking-tighter leading-[1.05]">
            Delivery leadership runs on control.
          </h1>
          <p className="mt-6 text-sm text-white/60 max-w-sm">
            Sign in to manage candidates, enquiries and website content.
          </p>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
          Authorised access only
        </div>
      </div>

      <div className="lg:col-span-7 p-6 lg:p-16 flex items-center justify-center">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md border border-border p-8 lg:p-10 bg-white"
          data-testid="admin-login-form"
        >
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-8">Sign in</h2>
          {mfa ? (
            <MfaStep mfa={mfa} code={code} setCode={setCode} onBack={() => { setMfa(null); setCode(""); setError(""); }} />
          ) : (
            <>
          <label className="block mb-5">
            <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-swiss"
              data-testid="admin-login-email"
            />
          </label>
          <label className="block mb-5">
            <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-swiss"
              data-testid="admin-login-password"
            />
          </label>
              <div className="mb-5 -mt-2 text-right">
                <Link to="/admin/forgot-password" className="text-xs text-execution hover:underline" data-testid="admin-login-forgot-link">
                  Forgot password?
                </Link>
              </div>
            </>
          )}
          {error && (
            <div className="mb-5 text-sm text-destructive" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {submitting ? (mfa ? "Verifying..." : "Signing in...") : (mfa ? "Verify code" : "Sign in")}
          </button>
        </form>
      </div>
    </div>
  );
}

function MfaStep({ mfa, code, setCode, onBack }) {
  return (
    <div className="mb-5" data-testid="admin-login-mfa-step">
      <p className="text-sm text-muted-foreground mb-5" data-testid="admin-login-mfa-sent-to">
        We sent a 6-digit code to <strong className="text-foreground">{mfa.sent_to}</strong>. It expires in 10 minutes.
      </p>
      <label className="block mb-3">
        <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Verification code</span>
        <input
          required
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="input-swiss tracking-[0.4em] font-mono"
          data-testid="admin-login-mfa-code"
        />
      </label>
      <button type="button" onClick={onBack} className="text-xs text-execution hover:underline" data-testid="admin-login-mfa-back">
        Use a different account
      </button>
    </div>
  );
}
